import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

export const About = () => {
  const CONTENT = useSettings();
  const STATS = CONTENT.stats || [];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} className="relative py-28 md:py-40 bg-zinc-950" data-testid="about">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <motion.p
            variants={reveal} custom={0} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
            className="text-xs tracking-[0.3em] uppercase text-amber-500 font-bold mb-5"
          >
            {CONTENT.about.chapter}
          </motion.p>
          <motion.h2
            variants={reveal} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
            className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-5xl leading-[0.95] text-navy-dark"
          >
            {CONTENT.about.heading}
          </motion.h2>
          <motion.p
            variants={reveal} custom={2} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
            className="mt-7 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl"
          >
            {CONTENT.about.paragraphs[0]}
          </motion.p>
          <motion.p
            variants={reveal} custom={3} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
            className="mt-5 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl"
          >
            {CONTENT.about.paragraphs[1]}
          </motion.p>

          <div className="mt-12 grid grid-cols-3 gap-6 border-t border-slate-200 pt-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                variants={reveal} custom={i + 4} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }}
                data-testid={`about-stat-${i}`}
              >
                <div className="font-display font-black text-3xl md:text-4xl text-amber-500">{s.value}</div>
                <div className="mt-1 text-xs md:text-sm text-zinc-500 uppercase tracking-wider">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative rounded-md overflow-hidden aspect-[4/5] max-h-[640px] border border-slate-200 shadow-xl shadow-slate-200/60">
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.driverPortrait}
              alt="NTOA member truck driver at golden hour"
              className="absolute inset-0 w-full h-[116%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a1424]/75 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="inline-block bg-amber-500 text-zinc-950 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm">
                {CONTENT.about.badge}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
